import i18n from 'i18next';
import { api } from './api';
import type { Role, User } from '@/types';

function currentLocale(): 'ar' | 'en' {
  return i18n.language === 'en' ? 'en' : 'ar';
}

export interface SendOtpResponse {
  sent: true;
  expiresInSeconds: number;
}

export interface VerifyOtpResponse {
  user: User;
  accessToken?: string;
}

// The server emits snake_case fields on `/auth/*` payloads (notifications_enabled,
// phone_number, is_banned). The client User type uses camelCase. Normalize once
// here so callers can rely on a single shape.
function normalizeUser(raw: any): User {
  if (!raw) return raw as User;
  return {
    id: raw.id,
    phone: raw.phone ?? raw.phone_number ?? '',
    role: raw.role,
    hasProfile: raw.hasProfile ?? raw.has_profile ?? false,
    notificationsEnabled:
      raw.notificationsEnabled ?? raw.notifications_enabled ?? true,
    createdAt: raw.createdAt ?? raw.created_at ?? '',
    isBanned: raw.isBanned ?? raw.is_banned ?? false,
  };
}

export async function sendOtp(phone: string): Promise<SendOtpResponse> {
  const { data } = await api.post<SendOtpResponse>('/auth/send-otp', {
    phone_number: phone,
    locale: currentLocale(),
  });
  return data;
}

export async function verifyOtp(phone: string, code: string): Promise<VerifyOtpResponse> {
  const { data } = await api.post<VerifyOtpResponse>('/auth/verify-otp', {
    phone_number: phone,
    code,
    locale: currentLocale(),
  });
  return { ...data, user: normalizeUser(data.user) };
}

export async function setNotifications(enabled: boolean): Promise<User> {
  return setNotificationsEnabled(enabled);
}

export async function refresh(): Promise<{ accessToken: string }> {
  const { data } = await api.post<{ accessToken: string }>('/auth/refresh');
  return data;
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout');
}

export async function me(): Promise<User> {
  const { data } = await api.get<{ user: unknown }>('/auth/me');
  return normalizeUser(data.user);
}

export async function setRole(role: Role): Promise<User> {
  const { data } = await api.post<{ user: unknown }>('/auth/role', { role });
  return normalizeUser(data.user);
}

// Tier 7 (2026-04-16): optional exit-survey disposition can be bundled with
// the delete so it lands in the same transaction. Survey is strictly optional
// — if omitted, the delete still succeeds.
export interface DeleteAccountInput {
  disposition?: {
    outcome: string;
    note?: string | null;
  };
}

export async function deleteAccount(body?: DeleteAccountInput): Promise<void> {
  await api.delete('/auth/account', { data: body ?? {} });
}

export async function setNotificationsEnabled(enabled: boolean): Promise<User> {
  const { data } = await api.patch<{ user: unknown }>('/auth/notifications', { enabled });
  return normalizeUser(data.user);
}

export async function setLocale(locale: 'ar' | 'en'): Promise<void> {
  await api.patch('/auth/locale', { locale });
}
